module.exports = {
    theme: {
        screens: {
            sm: '640px',
            md: '768px',
            lg: '1024px',
            xl: '1280px',
            '2xl': '1450px',
            // breakpoint that is active up until a max width of 1280px
            'max-xl': {
                max: '1280px',
            },
        },
        extend: {
            borderWidth: {
                12: '12px',
            },
            width: {
                'main-xl': '1038px',
            },
        },
        fontFamily: {
            body: ['Montserrat', 'Verdana', 'Arial', 'sans-serif'],
        },
        fontSize: {
            xs: '0.75rem',
            sm: '0.875rem',
            base: '1rem',
            lg: '1.125rem',
            xl: '1.25rem',
            '2xl': '1.5rem',
            '3xl': '1.875rem',
            '4xl': '2.25rem',
            '5xl': '3rem',
            '6xl': '4rem',
        },
        colors: {
            transparent: 'transparent',
            black: '#000000',
            white: '#FFFFFF',
            offwhite: '#F7F7FF',
            // Purple
            primary: {
                100: '#D4C9F4',
                200: '#8C82A9',
                300: '#C1AAFF',
                400: '#8769EB',
                500: '#703DFB',
                600: '#6231EA',
                700: '#572AD4',
            },
            // Purple tones used in buttons. If you encounter one of these outside of a button,
            // move it into 'primary' above and find/rename usages as necessary.
            'outlined-primary': {
                hover: '#F2EDFF',
                active: '#E1D8FB',
                focus: '#F0EBFD',
                disabled: '#EBF1F2',
                'disabled-border': '#D7DCDD',
                'disabled-text': '#8FA4A6',
            },
            // Aqua / Teal
            secondary: {
                100: '#D5F1F4',
                200: '#8FA4A6',
                400: '#A9E0EB',
                500: '#3FC2CE',
                600: '#06B1C1',
            },
            // Gray
            tertiary: {
                100: '#E7EAF8',
                200: '#CFD5E6',
                300: '#AAA9AD',
                400: '#74788D',
                500: '#39393D',
                800: '#1F1F21',
                900: '#1B1B1B',
            },
            // Blue
            quarternary: {
                100: '#D8E3FF',
                200: '#ADBBEB',
                500: '#6D9DF6',
                600: '#B8BEE2',
                700: '#949CC6',
            },
            // Coral / orange-red
            quinary: {
                100: '#FFE6E6',
                400: '#FF7572',
                500: '#FD7776',
                700: '#FC504F',
            },
            // Other specific colors
            highlight: {
                orange: '#E5683D',
                paleorange: '#FF9A77',
                teal: '#52FFCC',
                darkblue: '#0B1593',
                palelavender: '#F7F8FF',
                fadedpurple: '#EBE3FF',
                gold: '#FFCC72',
                mediumpurple: '#9E48E0',
                magenta: '#D8386E',
            },
        },
    },
    purge: {
        // enable purgecss for all environments except local development
        enabled: process.env.NODE_ENV !== 'development',
        content: [
            './src/*.html',
            './src/**/*.html',
            './src/*.ts?(x)',
            './src/**/*.ts?(x)',
            './src/*.js?(x)',
            './src/**/*.js?(x)',
            '../components/src/**/*.ts?(x)',
            '../components/src/*.ts?(x)',
        ],
    },
    plugins: [],
    variants: {
        accessibility: ['responsive', 'focus'],
        alignContent: ['responsive'],
        alignItems: ['responsive'],
        alignSelf: ['responsive'],
        appearance: ['responsive'],
        backgroundAttachment: ['responsive'],
        backgroundClip: ['responsive'],
        backgroundColor: ['responsive', 'hover', 'focus', 'disabled'],
        backgroundImage: ['responsive'],
        gradientColorStops: ['responsive', 'hover', 'focus'],
        backgroundOpacity: ['responsive', 'hover', 'focus'],
        backgroundPosition: ['responsive'],
        backgroundRepeat: ['responsive'],
        backgroundSize: ['responsive'],
        borderCollapse: ['responsive'],
        borderColor: ['responsive', 'hover', 'focus', 'disabled'],
        borderOpacity: ['responsive', 'hover', 'focus'],
        borderRadius: ['responsive'],
        borderStyle: ['responsive', 'disabled'],
        borderWidth: ['responsive'],
        boxShadow: ['responsive', 'hover', 'focus'],
        boxSizing: ['responsive'],
        container: ['responsive'],
        cursor: ['responsive', 'disabled'],
        display: ['responsive'],
        divideColor: ['responsive'],
        divideOpacity: ['responsive'],
        divideStyle: ['responsive'],
        divideWidth: ['responsive'],
        fill: ['responsive'],
        flex: ['responsive'],
        flexDirection: ['responsive'],
        flexGrow: ['responsive'],
        flexShrink: ['responsive'],
        flexWrap: ['responsive'],
        float: ['responsive'],
        clear: ['responsive'],
        fontFamily: ['responsive'],
        fontSize: ['responsive'],
        fontSmoothing: ['responsive'],
        fontStyle: ['responsive', 'disabled'],
        fontWeight: ['responsive', 'hover', 'focus'],
        height: ['responsive'],
        inset: ['responsive'],
        justifyContent: ['responsive'],
        letterSpacing: ['responsive'],
        lineHeight: ['responsive'],
        listStylePosition: ['responsive'],
        listStyleType: ['responsive'],
        margin: ['responsive'],
        maxHeight: ['responsive'],
        maxWidth: ['responsive'],
        minHeight: ['responsive'],
        minWidth: ['responsive'],
        objectFit: ['responsive'],
        objectPosition: ['responsive'],
        opacity: ['responsive', 'hover', 'focus'],
        order: ['responsive'],
        outline: ['responsive', 'focus'],
        overflow: ['responsive'],
        overscrollBehavior: ['responsive'],
        padding: ['responsive'],
        placeholderColor: ['responsive', 'focus'],
        placeholderOpacity: ['responsive', 'focus'],
        pointerEvents: ['responsive'],
        position: ['responsive'],
        resize: ['responsive'],
        space: ['responsive'],
        stroke: ['responsive'],
        strokeWidth: ['responsive'],
        tableLayout: ['responsive'],
        textAlign: ['responsive'],
        textColor: ['responsive', 'hover', 'focus', 'disabled', 'active'],
        textOpacity: ['responsive', 'hover', 'focus'],
        textDecoration: ['responsive', 'hover', 'focus'],
        textTransform: ['responsive'],
        userSelect: ['responsive'],
        verticalAlign: ['responsive'],
        visibility: ['responsive'],
        whitespace: ['responsive'],
        width: ['responsive'],
        wordBreak: ['responsive'],
        zIndex: ['responsive'],
        gap: ['responsive'],
        gridAutoFlow: ['responsive'],
        gridTemplateColumns: ['responsive'],
        gridColumn: ['responsive'],
        gridColumnStart: ['responsive'],
        gridColumnEnd: ['responsive'],
        gridTemplateRows: ['responsive'],
        gridRow: ['responsive'],
        gridRowStart: ['responsive'],
        gridRowEnd: ['responsive'],
        transform: ['responsive'],
        transformOrigin: ['responsive'],
        scale: ['responsive', 'hover', 'focus'],
        rotate: ['responsive', 'hover', 'focus'],
        translate: ['responsive', 'hover', 'focus'],
        skew: ['responsive', 'hover', 'focus'],
        transitionProperty: ['responsive'],
        transitionTimingFunction: ['responsive'],
        transitionDuration: ['responsive'],
        transitionDelay: ['responsive'],
        animation: ['responsive'],
    },
};
